import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { EdpModalService } from "@edp-components/edp-modal";
import { ActiveMeters, Reading } from "../edp-consumptions.models";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { Subject } from "rxjs";
import { getActiveMetersElectricity } from "@edp-store/selectors/dashboard.selectors";
import { takeUntil } from "rxjs/operators";
import { RegisterTypeEnum } from "@edp-enums/register-type.enum";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import { trigger, state, style, animate, transition } from "@angular/animations";
import { AppService } from "@edp-services/app.service";
import { MobileResponse } from "@edp-models/app.model";
import { DeviceDetectorService } from "ngx-device-detector";
import { OperationHistoryService } from "@edp-services/operation-history.service";

@Component({
    selector: "app-edp-widget-consumptions-voice-reading",
    templateUrl: "./edp-widget-consumptions-voice-reading.component.html",
    styleUrls: ["./edp-widget-consumptions-voice-reading.component.scss"],

    animations: [
        trigger("btnMic", [
            state("initial", style({
                marginLeft: "calc(50% - 1.5rem)",
            })),
            state("final", style({
                marginLeft: 0,
            })),
            transition("initial => final", animate("350ms")),
            transition("final => initial", animate("250ms"))
        ]),
        trigger("btnSendReading", [
            state("initial", style({
                display: "none",
                opacity: 0
            })),
            state("final", style({
                opacity: 1
            })),
            transition("initial => final", [
                style({ display: "block" }),
                animate("350ms")
            ]),
            transition("final => initial", animate("0ms"))
        ])
    ]
})
export class EdpWidgetConsumptionsVoiceReadingComponent implements OnInit, OnDestroy {

    @ViewChild("meterList", {read: ElementRef, static: false}) meterList: ElementRef;

    public activeMetersElectricity: ActiveMeters;
    public readingTypes = [];
    public readingsValidation = false;
    public tryAgain = false;
    public isListening = false;
    public iOSdevice = false;

    private unsubscribe = new Subject();
    private activatedReadingTypesArrayIndex = 0;
    private contract: Contract = null;
    private hasPermission = false;

    constructor(
        private deviceDetector: DeviceDetectorService,
        private edpModalService: EdpModalService,
        private store: Store<IAppState>,
        private changeDetector: ChangeDetectorRef,
        public appService: AppService,
        private operationHistoryService: OperationHistoryService
        ) {
        this.iOSdevice = this.deviceDetector.getDeviceInfo().os.toLowerCase() === "ios";
    }

    ngOnInit() {
        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getContract)
        ).subscribe(contract => {
            if (contract) {
                this.contract = contract;
            }
        });

        this.store.pipe(
            takeUntil(this.unsubscribe),
            select(getActiveMetersElectricity)
        ).subscribe((meters: ActiveMeters[]) => {
            if (!!meters && meters.length) {
                this.activeMetersElectricity = meters[0];
            }
        });

        this.readingTypes = this.activeMetersElectricity.meters.map(
            x => Object.assign({
                reading: "...",
                outOfBounds: false,
                lastReading: null
            }, x)
        );

        this.readingTypes.forEach(meter => {

            let lastRead = null;
            switch (meter.RegisterType) {
                case RegisterTypeEnum[RegisterTypeEnum.S]:
                    lastRead = this.activeMetersElectricity.lastInserted.S; break;
                case RegisterTypeEnum[RegisterTypeEnum.FV]:
                    lastRead = this.activeMetersElectricity.lastInserted.FV; break;
                case RegisterTypeEnum[RegisterTypeEnum.V]:
                    lastRead = this.activeMetersElectricity.lastInserted.V; break;
                case RegisterTypeEnum[RegisterTypeEnum.C]:
                    lastRead = this.activeMetersElectricity.lastInserted.C; break;
                case RegisterTypeEnum[RegisterTypeEnum.P]:
                    lastRead = this.activeMetersElectricity.lastInserted.P; break;
                case RegisterTypeEnum[RegisterTypeEnum.LT]:
                    lastRead = this.activeMetersElectricity.lastInserted.LT; break;
                default: break;
            }
            meter.lastReading = lastRead;
        });

        this.callEvent("isRecognitionAvailable");
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public dismiss(): void {
        this.edpModalService.close(false);
    }

    public onMicPush() {
        if (this.hasPermission) {
            this.validateEvent();
        } else {
            this.validateEvent("requestPermissionListening");
        }
    }

    private validateEvent(eventName?: string) {
        if (eventName) {
            this.callEvent(eventName);
        } else if (!this.isListening) {
            this.startListening();
        } else {
            this.endedListening();
        }
    }

    private callEvent(eventName: string) {
        this.appService.speechRecognition(eventName).subscribe(this.speechResponse.bind(this));
    }

    private speechResponse(data: MobileResponse) {
        if (data !== null) {
            switch (data.operation) {
                case "RECOGNITIONAVAILABLE":
                    if (data.info) {
                        this.validateEvent("hasPermissionListening");
                    }
                    break;
                case "VOICEPERMISSION":
                    this.hasPermission = data.info;
                    if (!data.info) {
                        this.validateEvent("requestPermissionListening");
                    }
                    break;
                case "VOICEREQUESTPERMISSION":
                    this.hasPermission = data.info;
                    break;
                case "VOICERESULT":
                    if (data.info.length > 0) {
                        this.manageListening(data.info);
                    }
                    this.endedListening();
                    break;
                case "VOICEERROR":
                    if (data.info === "request permission plugin") {
                        this.hasPermission = false;
                    } else {
                        this.endedListening();
                    }
                    break;
                default: break;
            }
        }
    }

    public onSubmitReading() {
        const readings: Reading[] = [];
        const extUi = this.contract.BasicComponent
            .filter(component => component.SupplyType === this.activeMetersElectricity.supplyType)
            .map(x => x.DeliveryPoint)[0];

        this.readingTypes.forEach(meter => {
            readings.push(new Reading(
                meter.Brand,
                meter.Material,
                meter.Model,
                meter.Serialno,
                meter.Register,
                meter.RegisterType,
                meter.RegisterTypeStr,
                meter.reading,
                extUi
            ));
        });

        const args: any = {
            contractId: this.contract.ContractId,
            SupplyType: this.activeMetersElectricity.supplyType,
            ReadingsList: readings,
            Origin: "voice"
        };

        this.edpModalService.close(args);
    }

    private manageListening(spokenResults: any) {
        this.tryAgain = false;
        this.detectActivatedMeterElement();
        const activatedReadingType = this.readingTypes[this.activatedReadingTypesArrayIndex];
        activatedReadingType.outOfBounds = false;

        const spokenValue = this.detectSpokenNumber(spokenResults);
        if (spokenValue !== null) {
            activatedReadingType.reading = spokenValue;
            if (Number(spokenValue) > Number(activatedReadingType.MaxRead) ||
                Number(spokenValue) < Number(activatedReadingType.MinRead)) {
                activatedReadingType.outOfBounds = true;
            }
        } else {
            activatedReadingType.reading = "...";
            this.tryAgain = true;
        }
    }

    private startListening() {
        this.isListening = true;
        this.validateEvent("startListening");
    }

    private endedListening() {
        this.isListening = false;
        this.callEvent("stopListening");
        this.readingsValidation = this.validateReadings();
        this.detectChanges();
    }

    private detectChanges() {
        this.changeDetector.detectChanges();
    }

    private detectSpokenNumber(spokenValues: any) {
        if (spokenValues instanceof Array) {
            for (const value of spokenValues) {
                const preparedValue = this.prepareSpokenValue(value);
                if (preparedValue !== null) {
                    return this.prepareSpokenValue(value);
                }
            }
        } else {
            return this.prepareSpokenValue(spokenValues);
        }

        this.operationHistoryService.registerWithContract(
            this.contract.ContractId,
            "READING_VOICE_ERROR",
            this.activeMetersElectricity.supplyType,
            spokenValues ? spokenValues.toString() : null
        );
        return null;
    }

    private prepareSpokenValue(value: string) {
        const preparedValue = value.replace(/[\. ,:-]+/g, "");
        if (Number(preparedValue)) {
            return Number(preparedValue);
        }
        return null;
    }

    private detectActivatedMeterElement() {
        const attrib = "active-element";
        const ulChildrens = Array.from(this.meterList.nativeElement.children);

        ulChildrens.forEach((liChild: HTMLElement, index) => {
            if (liChild.attributes.getNamedItem(attrib) && liChild.attributes.getNamedItem(attrib).value === "true") {
                this.activatedReadingTypesArrayIndex = index;
            }
        });
    }

    private validateReadings() {
        const givenReadings =
            this.readingTypes.filter((item) => {
                return item.reading !== "...";
            }).length;

        if (givenReadings === this.readingTypes.length &&
            !this.isListening) {
            return true;
        }
        return false;
    }
}
